/* Cómo clasifica el TALLER a un acoplado.
 *
 * ── ESTO NO ES LA CARROCERÍA, Y CONFUNDIRLAS YA COSTÓ CARO ───────────────
 *
 * Hay DOS preguntas distintas sobre el mismo acoplado y cada una tiene su
 * columna:
 *
 * | `tipo_acoplado` | la CARROCERÍA — playo · furgón · portacontenedor.
 *                     La lee el alta de movimientos. Ver `tipoAcoplado.js` |
 * | `tipo_taller`   | cómo lo clasifica el taller para saber qué se le puede
 *                     hacer — prolongado · piso liso · pincen 120 · randon 120 |
 *
 * `/FurgonesApp` escribía los cuatro del taller en la columna de la
 * carrocería, cuyo `CHECK` sólo acepta los otros tres. **La base los
 * rechazaba, siempre**, y como nadie miraba el error el botón «no hacía nada»
 * y la columna seguía en 0 de 679. Fundirlas obligaría a elegir cuál de los
 * dos vocabularios pierde, y el que pierda deja a alguien sin poder decir lo
 * que sabe.
 *
 * ── POR QUÉ EL HUB LO MUESTRA ────────────────────────────────────────────
 *
 * Porque si no, **el trabajo de Federico es invisible**. Él carga el tipo
 * todos los días desde el taller y la única pantalla compartida —el padrón—
 * dibujaba `tipo_acoplado`, que va a quedar en «Sin dato» para siempre. Un
 * dato que se carga y nadie ve es un dato que se deja de cargar.
 *
 * ── Y «SIN DATO» ES UN ESTADO PROPIO ─────────────────────────────────────
 *
 * Nace con 678 de 679 sin cargar. Dibujado como una de las cuatro opciones,
 * la lista de prolongados arrancaría mintiendo y nadie tendría cómo notarlo.
 * Misma regla que la lona y que el tipo de servicio.
 *
 * EL GEMELO ESTÁ EN FLOTABOT: `furgones_taller.TIPOS` y `normalizar_tipo`,
 * que es lo que escribe la columna. Si tocás una, tocá la otra — y el `CHECK`
 * de la base es la tercera copia de la misma lista, así que agregar un tipo
 * son tres lugares.
 */

export const SIN_DATO = "sin_dato";

/* ── EL VOCABULARIO ES ABIERTO, Y `TIPOS` SON SUGERENCIAS ────────────────
 *
 * Nació con los cuatro de la hoja impresa de Federico. Al día siguiente Nico
 * nombró «baranda volcable» y cerró la lista con un «etc»: la lista NO se
 * conoce. Y midiendo el padrón aparece que además mezcla cuatro dimensiones
 * —fabricante (PINCEN, 37 filas en `marca`; RANDON, 2), baranda (3 filas con
 * «PLAYO BARANDA VOLCABLE» en `detalle`), piso y largo—.
 *
 * Con vocabulario abierto y sin confirmar, cerrarlo es inventar. Es la regla
 * de los clientes de `movimientos` («nadie fusiona RADIO VICTORIA con
 * R.VICTORIA») y la de `mecanicos`. Por eso el `CHECK` de la base se sacó: un
 * tipo nuevo volvía como rechazo y el botón «no hacía nada».
 *
 * Mismo orden y misma escritura que `furgones_taller.TIPOS`. */
export const TIPOS = [
  { clave: "prolongado", etiqueta: "Prolongado" },
  { clave: "piso liso", etiqueta: "Piso liso" },
  { clave: "pincen 120", etiqueta: "Pincen 120" },
  { clave: "randon 120", etiqueta: "Randon 120" },
  { clave: "baranda volcable", etiqueta: "Baranda volcable" },
];

/* De lo que hay en la fila al tipo.
 *
 * VACÍO es `SIN_DATO`; CUALQUIER OTRA COSA ENTRA TAL CUAL. Devolver `SIN_DATO`
 * ante lo que no está en la lista —que es lo que hacía— convierte una carga
 * buena en un hueco sin fallar en ningún lado, que es el mismo bug que tuvo el
 * botón de `/FurgonesApp`, un escalón más abajo.
 *
 * Lo CONOCIDO igual se canoniza, para que la lista no se disperse en
 * sinónimos y siga sirviendo para filtrar. Gemelo de
 * `furgones_taller.normalizar_tipo`. */
export function estadoTipoTaller(valor) {
  const v = String(valor ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!v) return SIN_DATO;
  const exacto = TIPOS.find(
    (t) => t.clave === v || t.clave.replace(/ /g, "") === v.replace(/ /g, "")
  );
  if (exacto) return exacto.clave;
  /* «prolongados» en plural, «pincen» sin el 120: se escribe como se habla. */
  const porRaiz = TIPOS.find((t) => v.startsWith(t.clave.split(" ")[0]));
  return porRaiz ? porRaiz.clave : v;
}

/* Los tipos de un furgón, ya canónicos y sin repetir. UNA LISTA, no uno.
 *
 * Federico lo pidió el 23/09: un furgón es «randon con caja Pincen» —dos
 * tipos—, y `/FurgonesApp` los guarda en `tipo_taller` separados por coma.
 * Gemelo de `furgones_taller.normalizar_tipos`. Vacío es lista vacía. */
export function estadosTipoTaller(valor) {
  const out = [];
  for (const parte of String(valor ?? "").split(",")) {
    const e = estadoTipoTaller(parte);
    if (e !== SIN_DATO && !out.includes(e)) out.push(e);
  }
  return out;
}

/* La etiqueta de UN tipo. Un tipo que no está en las sugerencias se muestra
 * COMO SE ESCRIBIÓ — esconderlo detrás de «Sin dato» haría invisible justo lo
 * que hay que revisar. */
function etiquetaDeUno(clave) {
  const t = TIPOS.find((x) => x.clave === clave);
  return t ? t.etiqueta : clave.charAt(0).toUpperCase() + clave.slice(1);
}

/* Cómo se dice. `sin dato` dice que falta cargarlo, no un tipo. Con varios,
 * se juntan con « · » —«Prolongado · Pincen 120»— porque son dos cosas ciertas
 * del mismo furgón, no una elección entre ellas. */
export function etiquetaTipoTaller(valor) {
  const claves = estadosTipoTaller(valor);
  if (!claves.length) return "Sin dato";
  return claves.map(etiquetaDeUno).join(" · ");
}

/* Los que NO están en las sugerencias, con cuántos hay de cada uno.
 *
 * Un vocabulario abierto sin esta lista se pudre solo: «pincén» con acento y
 * «pincen» serían dos tipos y nadie se enteraría. NO SE FUSIONAN —dos
 * parecidos pueden ser dos tipos distintos— se muestran, que es lo que hacen
 * `mecanicos.parecidos()` y `permisos_desconocidos()`. */
export function sinReconocer(furgones) {
  const conocidos = new Set(TIPOS.map((t) => t.clave));
  const out = new Map();
  for (const f of furgones || []) {
    /* POR ETIQUETA: un furgón «randon 120, pata rota» aporta al raro sin
     * esconder el tipo bueno del mismo furgón. */
    for (const e of estadosTipoTaller(f?.tipo_taller)) {
      if (conocidos.has(e)) continue;
      out.set(e, (out.get(e) || 0) + 1);
    }
  }
  return [...out.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([clave, n]) => ({ clave, n }));
}

/* Cuántos hay de cada uno, con los que faltan aparte.
 *
 * DEVUELVE `total` Y `faltan` POR SEPARADO: «9 prolongados» sobre una flota de
 * 679 se lee como un inventario terminado, y son 9 de los que alguien cargó
 * sobre seiscientos que nadie tocó. Misma regla que la lona. */
export function contar(furgones) {
  const out = { [SIN_DATO]: 0 };
  for (const t of TIPOS) out[t.clave] = 0;
  for (const f of furgones || []) {
    /* POR ETIQUETA: uno «prolongado, pincen 120» suma en los dos, así que los
     * conteos por tipo pueden sumar más que la flota. `faltan` sigue siendo por
     * furgón —cuántos no tienen ningún tipo— que es lo que hay que cargar. */
    const claves = estadosTipoTaller(f?.tipo_taller);
    if (!claves.length) out[SIN_DATO] += 1;
    for (const e of claves) out[e] = (out[e] || 0) + 1;
  }
  return {
    ...out,
    total: (furgones || []).length,
    faltan: out[SIN_DATO],
  };
}
